// Thin WebGL2 helpers: shader programs with cached uniform locations.

export type GL = WebGL2RenderingContext;

export class Program {
  readonly prog: WebGLProgram;
  private locs = new Map<string, WebGLUniformLocation | null>();

  constructor(readonly gl: GL, vsrc: string, fsrc: string, readonly name: string) {
    const vs = compile(gl, gl.VERTEX_SHADER, vsrc, name);
    const fs = compile(gl, gl.FRAGMENT_SHADER, fsrc, name);
    const p = gl.createProgram()!;
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error(`Link failed (${name}): ${gl.getProgramInfoLog(p)}`);
    }
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    this.prog = p;
  }

  use() {
    this.gl.useProgram(this.prog);
    return this;
  }

  loc(name: string) {
    let l = this.locs.get(name);
    if (l === undefined) {
      l = this.gl.getUniformLocation(this.prog, name);
      this.locs.set(name, l);
    }
    return l;
  }

  attrib(name: string) {
    return this.gl.getAttribLocation(this.prog, name);
  }

  f1(n: string, v: number) { this.gl.uniform1f(this.loc(n), v); return this; }
  i1(n: string, v: number) { this.gl.uniform1i(this.loc(n), v); return this; }
  f2(n: string, a: number, b: number) { this.gl.uniform2f(this.loc(n), a, b); return this; }
  f3(n: string, a: number, b: number, c: number) { this.gl.uniform3f(this.loc(n), a, b, c); return this; }
  f4(n: string, a: number, b: number, c: number, d: number) { this.gl.uniform4f(this.loc(n), a, b, c, d); return this; }
  v3(n: string, v: ArrayLike<number>) { this.gl.uniform3f(this.loc(n), v[0], v[1], v[2]); return this; }
  /** Upload a row-major 3x3 matrix. */
  m3(n: string, m: ArrayLike<number>) {
    this.gl.uniformMatrix3fv(this.loc(n), true, m instanceof Float32Array ? m : Float32Array.from(m));
    return this;
  }
}

function compile(gl: GL, type: number, src: string, name: string) {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s);
    const numbered = src.split('\n').map((l, i) => `${i + 1}: ${l}`).join('\n');
    throw new Error(`Shader compile failed (${name}): ${log}\n${numbered}`);
  }
  return s;
}

export function createBuffer(gl: GL, data: ArrayBufferView | ArrayBuffer, usage: number = gl.STATIC_DRAW, target: number = gl.ARRAY_BUFFER) {
  const b = gl.createBuffer()!;
  gl.bindBuffer(target, b);
  gl.bufferData(target, data as BufferSource, usage);
  return b;
}

export function loadTexture(gl: GL, url: string, opts: { repeatS?: boolean; mips?: boolean } = {}): Promise<WebGLTexture> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      const tex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      const mips = opts.mips !== false;
      if (mips) gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, mips ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, opts.repeatS ? gl.REPEAT : gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      resolve(tex);
    };
    img.onerror = () => reject(new Error(`Texture failed: ${url}`));
    img.src = url;
  });
}

/**
 * Shared GLSL: sky-frame transforms, refraction and the two projections.
 * u_proj 0 = perspective (gnomonic, used for AR so the overlay matches the camera),
 * u_proj 1 = stereographic (manual mode; keeps shapes true at wide angles).
 */
export const GLSL_COMMON = /* glsl */ `
uniform mat3 u_eqjToEnu;
uniform mat3 u_enuToCam;
uniform int u_proj;
uniform float u_scale;      // radians-to-NDC factor along the shorter screen side
uniform vec2 u_aspectFix;   // multiplies NDC so the shorter side spans the field of view
uniform vec2 u_viewport;    // pixels

float refrLift(float altDeg) {
  if (altDeg < -89.9) return 0.0;
  float hd = max(altDeg, -1.0);
  float r = (1.02 / tan(radians(hd + 10.3 / (hd + 5.11)))) / 60.0;
  if (altDeg < -1.0) r *= (altDeg + 90.0) / 89.0;
  return r;
}

vec3 refractEnu(vec3 d) {
  float alt = degrees(asin(clamp(d.z, -1.0, 1.0)));
  float a2 = radians(alt + refrLift(alt));
  float h = length(d.xy);
  if (h < 1e-6) return d;
  return vec3(d.xy * (cos(a2) / h), sin(a2));
}

// Returns NDC xy in .xy and a visibility flag in .z (1 visible, 0 culled).
vec3 projectCam(vec3 c) {
  vec2 p;
  if (u_proj == 0) {
    if (c.z > -0.02) return vec3(0.0, 0.0, 0.0);
    p = c.xy / -c.z;
  } else {
    float den = 1.0 - c.z;
    if (den < 0.03) return vec3(0.0, 0.0, 0.0);
    p = 2.0 * c.xy / den;
  }
  return vec3(p * u_scale * u_aspectFix, 1.0);
}
`;
